/**
 * Schemas for the venues marketing-city surface (API-1397+).
 *
 * Hosts query + response Zod shapes for the four split endpoints:
 *   - GET /api/v1/venues               (API-1397, list)
 *   - GET /api/v1/venues/:slug         (API-1398, detail)
 *   - GET /api/v1/venues/:slug/shows   (API-1399, paginated upcoming events)
 *   - GET /api/v1/venues/:slug/stats   (API-1400, derived stats)
 *
 * Endpoint shapes mirror specs/openapi/backend-api-v1.yaml (API-1395). The
 * cursor format and `cityCode` resolution are shared so all four endpoints
 * stay symmetrical with the neighborhoods surface.
 */

import { z } from 'zod';
import { CITY_EDITIONS } from '@loci/city-editions';
import { type VenueType } from './schemas';
import {
  VENUE_LIST_CATEGORY_SLUGS,
  VENUE_PAGE_CATEGORY_SLUGS,
  isKnownVenueListCategory,
  isKnownVenuePageCategory,
} from './venueListCategory';

// Pagination — values come from the OpenAPI contract (API-1395). The Linear
// issues for shows/stats predate the contract; the contract values win because
// the generated TS types follow the spec and clients trust those.
export const VENUES_DEFAULT_LIMIT = 24;
export const VENUES_MAX_LIMIT = 100;

/**
 * Default window `VenueListItem.upcomingCount` is computed over, in days: the
 * list route counts browse-visible events with
 * `date >= now AND date < now + N days`.
 *
 * API-1707: the live window now also rides on the response as
 * `meta.upcomingWindowDays` (and on `/venues/:slug/stats` as
 * `upcomingWindowDays`), so clients should prefer the response field. This
 * constant remains:
 *   - the server-side default used to compute the count, and
 *   - the Zod `.default(...)` / client fallback for older cached payloads
 *     during deploy skew.
 *
 * Do not invent a second numeric literal in apps/web for copy — read the
 * response field, falling back here only when it is absent.
 */
export const VENUES_UPCOMING_WINDOW_DAYS = 30;

/**
 * Window, in days, of the materialized `Location.eventYield28d` snapshot — the
 * key `?sort=upcoming` orders by (API-1705), and so the "28" in every label
 * the web app prints beside that ordering (WEB-896).
 *
 * Unlike {@link VENUES_UPCOMING_WINDOW_DAYS}, this window is NOT echoed on the
 * wire: the snapshot is written out-of-band by data-pipeline's
 * `refreshEventYields` (`DEFAULT_EVENT_YIELD_WINDOW_DAYS`, overridable with
 * `EVENT_YIELD_REFRESH_WINDOW_DAYS`), so this constant is the one place a
 * client reads it from. It lives here, beside the `eventYield28d` field it
 * describes, rather than as a second numeric literal in apps/web. Keep it in
 * step with the pipeline's default.
 */
export const VENUES_EVENT_YIELD_WINDOW_DAYS = 28;

/**
 * Public `cityCode` → the `Location.city` value Prisma filters on, for every
 * venue endpoint.
 *
 * A **derived view** of the launched-market registry (API-1704), not a map to
 * edit. Before API-1704 this was hand-maintained beside a second hand-maintained
 * `VENUE_CITY_TIMEZONE_MAP`, and the two could disagree — which is why the venue
 * routes carried a 500 branch for "known city, no timezone". Both facts now come
 * off one {@link CityEdition}, so that disagreement is unrepresentable and the
 * branch is gone.
 *
 * Adding a market: add the record in `packages/city-editions/src/cities.ts`, then
 * register its edition. Registering the edition is what makes the code servable here —
 * and it necessarily brings the city's timezone with it.
 */
export const VENUE_CITY_CODE_MAP: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.values(CITY_EDITIONS).map((edition) => [edition.code, edition.locationCity]),
  ),
);

export const isKnownVenueCityCode = (code: string): boolean =>
  Object.prototype.hasOwnProperty.call(VENUE_CITY_CODE_MAP, code);

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const venueCityCodeSchema = z
  .string({ required_error: 'cityCode is required' })
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2,10}$/, 'cityCode must be 2-10 uppercase letters');

export const venueSlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9-]+$/, 'slug must contain only lowercase letters, numbers, and hyphens');

/**
 * Path param for GET /venues/:slug and its /shows + /stats siblings.
 *
 * Canonical URLs use `venueSlugSchema`. Category-hub "SEE VENUE" links
 * historically used `Location.id` (WEB-803): Prisma `cuid()` values pass
 * `venueSlugSchema` but miss the slug lookup, and fixture ids like
 * `loc_*` fail it outright (underscore → 400). Accept both shapes so
 * either URL resolves; handlers look up `slug OR id`.
 */
export const venueLookupParamSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z0-9_-]+$/,
    'venue lookup must contain only lowercase letters, numbers, hyphens, and underscores',
  );

const venueNeighborhoodSlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(
    /^[a-z0-9-]+$/,
    'neighborhoodSlug must contain only lowercase letters, numbers, and hyphens',
  );

/**
 * Code points of `Location.name` that take part in the `sort=name` ordering,
 * and therefore the most that a `/venues` cursor ever carries (WEB-876).
 *
 * `Location.name` is an unbounded Prisma `String` (Postgres `text`), so before
 * WEB-876 the cursor grew with the venue name and a long enough row minted a
 * cursor the API's own {@link VENUE_CURSOR_MAX_LENGTH} then rejected — a 400
 * that `fetchVenueList` degrades to an empty envelope, silently returning the
 * reader to page one and making every venue sorting after that row unreachable
 * by following links. That is WEB-870's defect re-introduced by one long row.
 *
 * Bounding the ordering key rather than the cursor is what makes the fix
 * total: cursor size becomes independent of name length, so the guarantee
 * holds for a name of ANY length instead of for names under some cap. The
 * ordering is `(left(name, 120), id)` — still a strict total order, because
 * `id` is unique — so the keyset predicate reproduces it exactly, with no
 * repeated and no skipped rows.
 *
 * Truncation is invisible in practice, and deliberately generous so that it
 * stays that way: the longest listable Charlotte venue name measured on
 * production (2026-09-10) is 110 code points — "Hauser Alumni Pavilion
 * presented by Parks Chevrolet Charlotte, Official Hometown Dealer of the
 * Charlotte 49ers" — and it is the only one over 100. At 120 no row in the
 * catalogue is truncated at all, so today's page order is byte-identical to
 * the pre-WEB-876 order. Two names would have to agree on their first 120
 * code points before the ordering between them changes, and even then they
 * merely fall back to the `id` tiebreaker the ordering already ends in.
 *
 * Do NOT lower this to shrink the cursor. Below roughly 40 code points real
 * Charlotte venues start sharing prefixes ("Charlotte Convention Center …"),
 * and each collision replaces alphabetical order with `id` order for that
 * group — visible on the page, for no benefit the cap does not already give.
 */
export const VENUE_NAME_SORT_KEY_MAX_CHARS = 120;

/**
 * The `sort=name` ordering key for a venue: its name truncated to
 * {@link VENUE_NAME_SORT_KEY_MAX_CHARS} code points.
 *
 * MUST agree exactly with the `left(<name>, 120)` that `venueListOrderBySql`
 * and `venueListCursorFilterSql` emit in `@loci/db-core` — a cursor minted
 * from a key the ORDER BY does not produce is precisely the silent
 * repeat-and-skip a keyset paginator fails with while still answering 200.
 *
 * Iterating with the spread is load-bearing, not style. Postgres `left(s, n)`
 * counts **code points** (verified: `left('A😀BCDEF', 3)` → `'A😀B'`), while
 * `String.prototype.slice` counts UTF-16 code units — so `name.slice(0, 120)`
 * would disagree with the database for any name carrying an astral character
 * (emoji are routine in scraped Eventbrite venue names) and could additionally
 * cut a surrogate pair in half. `[...name]` iterates code points, which is the
 * same unit Postgres uses.
 *
 * `packages/db-core/tests/venueNameCursorBound.db.test.ts` pins the agreement
 * against a real Postgres; no mocked test can see this class of mismatch.
 */
export const venueNameSortKey = (name: string): string =>
  [...name].slice(0, VENUE_NAME_SORT_KEY_MAX_CHARS).join('');

/**
 * Maximum accepted length of an encoded `?cursor=` value.
 *
 * Derived from {@link VENUE_NAME_SORT_KEY_MAX_CHARS} rather than picked, so
 * that every cursor the API can mint is one the API accepts back. Worst case,
 * in bytes, for the largest payload (`{"n":…,"i":…}` under `sort=name`):
 *
 * ```
 *    15   JSON frame: {"n":"","i":""}
 * +  720   name sort key: 120 code points x 6 bytes
 * +   64   Location.id budget (cuid() is 25; 64 leaves room for any writer)
 * -------
 *   799 bytes  ->  ceil(799 * 4 / 3) = 1066 base64url chars (padding stripped)
 * ```
 *
 * Six bytes per code point is the true ceiling for `JSON.stringify`, not a
 * guess: a C0 control character and a lone surrogate each escape to `\uXXXX`
 * (6 bytes), while an astral code point is emitted raw as 4 UTF-8 bytes.
 *
 * 1200 leaves headroom over that 1066 without being a blank cheque. The cap
 * still does its original job — rejecting an oversized query-string value
 * cheaply, before any decode work — it simply no longer rejects the API's own
 * output. `sort=upcoming` cursors are far smaller (`{s,y,i}`, all bounded).
 *
 * Exported because `apps/web` enforces the same ceiling on each segment of its
 * cursor trail; importing this is what stops the two from drifting apart, and
 * a web-side cap BELOW the API's is itself a way to lose the last pages.
 */
export const VENUE_CURSOR_MAX_LENGTH = 1200;

const venueCursorSchema = z.string().max(VENUE_CURSOR_MAX_LENGTH);
const venueLimitSchema = z.coerce
  .number()
  .int()
  .min(1, 'limit must be >= 1')
  .max(VENUES_MAX_LIMIT, `limit must be <= ${VENUES_MAX_LIMIT}`)
  .default(VENUES_DEFAULT_LIMIT);

export const venuePaginationMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  cursor: z.string().nullable(),
  limit: z.number().int().positive(),
  /**
   * Days the live `upcomingCount` on each item was computed over (API-1707).
   * `.default(VENUES_UPCOMING_WINDOW_DAYS)` so a web deploy that lands ahead of
   * backend-api (or an s-maxage/SWR cached body) does not fail `safeParse` and
   * blank the venues index — same deploy-skew pattern as `postalCode`.
   */
  upcomingWindowDays: z.number().int().positive().default(VENUES_UPCOMING_WINDOW_DAYS),
});
export type VenuePaginationMeta = z.infer<typeof venuePaginationMetaSchema>;

/**
 * API-1735 — 1-based inclusive ordinals of the returned slice inside the
 * current filtered catalogue (same `WHERE`/`ORDER BY` as the rows, **not**
 * trail depth).
 *
 * Keys are `.optional()` on purpose: `apps/web` `safeParse`s this schema and
 * types fixtures as `VenueListResponse`. Required keys (or `.default(0)`)
 * would type-error every web fixture that predates this field and, with a
 * default, fill `offset: 0` onto a page-N payload during deploy skew so WEB
 * would render "Showing 1–N" over the wrong rows. Absent keys mean "this
 * body was minted before API-1735"; WEB keeps trail-depth until the key is
 * present. The list route always writes all three.
 */
export const venueListPaginationMetaSchema = venuePaginationMetaSchema.extend({
  /** 0-based index of the first returned row in the filtered result set. */
  offset: z.number().int().nonnegative().optional(),
  /** 1-based inclusive ordinal of the first returned row; `0` when the page is empty. */
  pageStart: z.number().int().nonnegative().optional(),
  /** 1-based inclusive ordinal of the last returned row; `0` when the page is empty. */
  pageEnd: z.number().int().nonnegative().optional(),
});
export type VenueListPaginationMeta = z.infer<typeof venueListPaginationMetaSchema>;

/**
 * Derive `pageStart` / `pageEnd` from a 0-based offset and the page length.
 *
 * Empty pages keep `offset` (how many rows currently sort at or before the
 * cursor) and set both ordinals to `0` so a banner does not claim "Showing
 * N–N-1". WEB should prefer these fields over `trailDepth * pageSize` when
 * `pageStart` is a positive number.
 */
export const venueListPageRange = (
  offset: number,
  pageLength: number,
): { offset: number; pageStart: number; pageEnd: number } => {
  if (pageLength <= 0) {
    return { offset, pageStart: 0, pageEnd: 0 };
  }
  return { offset, pageStart: offset + 1, pageEnd: offset + pageLength };
};

// ---------------------------------------------------------------------------
// API-1397: GET /api/v1/venues (list)
// ---------------------------------------------------------------------------

const venueListCategorySchema = z.enum(VENUE_LIST_CATEGORY_SLUGS);

/**
 * `?category=` accepts the **union** of two vocabularies (API-1692):
 *   - the 8 `/venues` page chips (`VENUE_PAGE_CATEGORY_SLUGS`) — a programming
 *     facet: what a venue puts on;
 *   - the 12 shelves (`VENUE_LIST_CATEGORY_SLUGS`) — what kind of place it is,
 *     else how big the room is (the six type shelves joined in WEB-958).
 *
 * The union is unambiguous: they collide only on `outdoor` and `breweries`,
 * where both mean the same venue-intrinsic thing (pinned by
 * `venueListCategory.test.ts`). Keeping the tiers means no existing
 * `?category=mid` caller breaks.
 *
 * A value in **neither** vocabulary still resolves to `undefined`, i.e. the
 * request is served unfiltered with a `200` rather than a `400`. That silent
 * drop is deliberate and pre-dates API-1692 (`list.test.ts` "ignores an unknown
 * category slug and returns the unfiltered set", API-1583): a chip URL is a
 * navigational surface, and a stale bookmark should degrade to the full list,
 * not to an error page. What API-1692 fixes is that the 8 slugs the page
 * actually emits were *all* landing in that branch.
 */
const venueListCategoryQuerySchema = z
  .string()
  .trim()
  .optional()
  .transform((value) => {
    if (value === undefined || value === '') return undefined;
    if (isKnownVenueListCategory(value) || isKnownVenuePageCategory(value)) return value;
    return undefined;
  });

/**
 * `?sort=` vocabulary for `GET /api/v1/venues` (API-1705).
 *
 * `name` — the historical `(name, id)` ordering, and still the default: a
 * caller that sends no `sort` gets byte-identical behaviour to before API-1705.
 *
 * `upcoming` — orders by `Location.eventYield28d DESC NULLS LAST, id ASC`: the
 * **materialized** 28-day upcoming-event snapshot PIP-164 refreshes, not the
 * live 30-day `upcomingCount` the same route computes per row.
 *
 * That distinction is the whole reason this sort is implementable. API-1696
 * correctly refused to sort on the live LATERAL aggregate: it changes while a
 * client is paging, so a keyset cursor over it repeats and skips rows by
 * construction, and it cannot use an index. A snapshot column does neither —
 * it is fixed between page 1 and page 29, and it has a btree behind it
 * (`locations_event_yield_28d_desc_id_idx`).
 *
 * The cost of that choice is that the ordering key and the displayed
 * `upcomingCount` have different owners, windows (28d vs 30d) and freshness,
 * so they can disagree slightly. API-1705 resolves the disagreement by
 * publishing the snapshot too — see `eventYield28d` on {@link venueListItemSchema}
 * — so the number a reader sees is the number the order used. It does NOT
 * resolve it by ordering on the live aggregate.
 */
export const VENUES_LIST_SORTS = ['name', 'upcoming'] as const;
export type VenuesListSort = (typeof VENUES_LIST_SORTS)[number];

/** The ordering a caller gets when `?sort=` is absent. */
export const VENUES_LIST_DEFAULT_SORT: VenuesListSort = 'name';

/**
 * Unlike `?category=`, an unknown `?sort=` is a **400**, not a silent fallback.
 *
 * The two are different kinds of parameter. A category slug is navigational —
 * a stale chip bookmark should still land on a page of venues, which is why
 * `venueListCategoryQuerySchema` above drops unknown values. A sort is a
 * pagination key: it is encoded into every cursor this request mints, and
 * silently serving `name` for `?sort=upcomming` would hand the caller a trail
 * of cursors that then fail the sort-agreement check on the next page. Failing
 * on the first request is the legible outcome.
 */
const venuesListSortSchema = z.enum(VENUES_LIST_SORTS).default(VENUES_LIST_DEFAULT_SORT);

export const venuesListQuerySchema = z.object({
  cityCode: venueCityCodeSchema,
  neighborhoodSlug: venueNeighborhoodSlugSchema.optional(),
  category: venueListCategoryQuerySchema,
  sort: venuesListSortSchema,
  cursor: venueCursorSchema.optional(),
  limit: venueLimitSchema,
});
export type VenuesListQuery = z.infer<typeof venuesListQuerySchema>;

/**
 * WGS84 point for a venue. ONE coordinate shape on this resource: both
 * `venueListItemSchema` (API-1699) and `venueDetailResponseSchema` emit it,
 * so a consumer that can read a pin on one surface can read it on the other.
 * Declared here rather than beside the detail response because `const` is not
 * hoisted — the list schema below evaluates it at module load.
 */
export const venueCoordinatesSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type VenueCoordinates = z.infer<typeof venueCoordinatesSchema>;

export const venueListItemSchema = z.object({
  slug: z.string(),
  name: z.string(),
  cityCode: z.string(),
  neighborhoodSlug: z.string().nullable(),
  neighborhoodName: z.string().nullable(),
  capacity: z.number().int().nonnegative().nullable(),
  /**
   * `.catch(null)` is DEPLOY-SKEW tolerance (WEB-958), the same reasoning as
   * `eventYield28d`'s `.default(null)` below. Web (Vercel) and backend-api
   * (Railway) deploy with no ordering guarantee, and `fetchVenueList`
   * degrades a failed parse to an EMPTY envelope — so a strict enum here
   * turns a shelf slug this bundle does not know yet (a newer backend, or a
   * web rollback) into a blank `/venues` page for every reader. Read as
   * `null`, the row lands on "Other venues" instead: real, listed, and making
   * no shelf claim the bundle cannot render. The OUTPUT type is unchanged, so
   * every producer must still emit a known slug.
   */
  category: venueListCategorySchema.nullable().catch(null),
  upcomingCount: z.number().int().nonnegative(),
  /**
   * The materialized 28-day upcoming-event snapshot (`Location.eventYield28d`,
   * PIP-164) — the key `?sort=upcoming` orders by. `null` when the enrichment
   * run has not reached this venue yet (649 of 1,403 CLT rows as of
   * 2026-09-09); those venues still appear, ranked last.
   *
   * Published on EVERY item, not only under `?sort=upcoming`, and deliberately
   * NOT merged into `upcomingCount`. The two are different claims: this is a
   * refreshed-on-a-schedule 28-day snapshot owned by data-pipeline, while
   * `upcomingCount` is an exact 30-day count computed live in the request.
   * Collapsing them would have to sacrifice one — either the order stops
   * matching the number the reader sees, or an accurate live count is replaced
   * by a stale one. Publishing both is API-1705's answer to that wrinkle: the
   * number the order used is on the wire, next to the number that is true now.
   *
   * `.default(null)` rather than a bare `.nullable()`, and that is about DEPLOY
   * SKEW, not about the field being optional. The OUTPUT type stays required —
   * `VenueListItem` still forces every producer to supply it, and the route
   * does. What the default buys is the input side: `fetchVenueList` in the web
   * app `safeParse`s this schema and degrades a failed parse to an EMPTY
   * envelope, so while Vercel is running the new bundle against a Railway
   * backend that has not shipped this field yet, a bare `.nullable()` would
   * blank the public `/venues` index for the whole skew window. Tolerating an
   * absent key costs nothing and removes that window.
   */
  eventYield28d: z.number().int().nonnegative().nullable().default(null),
  primaryGenre: z.string().nullable(),
  hasPhoto: z.boolean(),
  /**
   * API-1699 — WGS84 point, or `null` when the row has no geometry.
   * `Location.coordinates` is nullable in schema, so `null` is a real value a
   * consumer must handle: never substitute a city-centre fallback (WEB-860).
   *
   * `.default(null)` guards the PARSE side only; it does not soften what the
   * API emits. The route always writes the key and the OpenAPI spec lists it
   * under `required`, but `apps/web/lib/server/fetchVenueList.ts` runs the
   * response through `venueListResponseSchema.safeParse` and degrades a parse
   * failure to an EMPTY page. Web (Vercel) and backend-api (Railway) deploy
   * from one merge but not atomically, and the endpoint ships
   * `s-maxage=300, stale-while-revalidate=900`, so a newly-deployed web app
   * will meet coordinate-less bodies for a bounded window either way. Without
   * the default that window blanks a live public surface for every row; with
   * it, the only degrade is a missing pin — which is the behaviour WEB-860
   * already prescribes for unknown geometry. The output type stays
   * `VenueCoordinates | null` (never optional), so consumers must still
   * handle the null.
   */
  coordinates: venueCoordinatesSchema.nullable().default(null),
});
export type VenueListItem = z.infer<typeof venueListItemSchema>;

export const venueListResponseSchema = z.object({
  venues: z.array(venueListItemSchema),
  meta: venueListPaginationMetaSchema,
});
export type VenueListResponse = z.infer<typeof venueListResponseSchema>;

// ---------------------------------------------------------------------------
// API-1695: GET /api/v1/venues/facets (chip counts + collection aggregates)
// ---------------------------------------------------------------------------

export const venueFacetsQuerySchema = z.object({
  cityCode: venueCityCodeSchema,
});
export type VenueFacetsQuery = z.infer<typeof venueFacetsQuerySchema>;

/** One neighborhood chip: its identity, its rail position, and its count. */
export const venueNeighborhoodFacetSchema = z.object({
  slug: z.string(),
  name: z.string(),
  position: z.number().int(),
  venues: z.number().int().nonnegative(),
});
export type VenueNeighborhoodFacet = z.infer<typeof venueNeighborhoodFacetSchema>;

/** One page-category chip and how many venues it would return. */
export const venueCategoryFacetSchema = z.object({
  slug: z.enum(VENUE_PAGE_CATEGORY_SLUGS),
  venues: z.number().int().nonnegative(),
});
export type VenueCategoryFacet = z.infer<typeof venueCategoryFacetSchema>;

/** One shelf (capacity tier or WEB-958 type shelf) and how many venues sit on it. */
export const venueTierFacetSchema = z.object({
  slug: z.enum(VENUE_LIST_CATEGORY_SLUGS),
  venues: z.number().int().nonnegative(),
});
export type VenueTierFacet = z.infer<typeof venueTierFacetSchema>;

/**
 * Collection-wide capacity aggregate (WEB-864).
 *
 * `min` / `max` are `null` — never `0` — while no venue in the set carries a
 * capacity. `0` would assert a real room that seats nobody; `null` says
 * "unknown" — the truth for every CLT venue until COR-79 / COR-83 backfill
 * `Location.capacity` (0 of 1,425 carry one, measured on the production branch
 * 2026-09-09). `known` is what tells a consumer whether a range is worth
 * rendering at all.
 */
export const venueCapacityFacetSchema = z.object({
  known: z.number().int().nonnegative(),
  min: z.number().int().nonnegative().nullable(),
  max: z.number().int().nonnegative().nullable(),
});
export type VenueCapacityFacet = z.infer<typeof venueCapacityFacetSchema>;

/**
 * `GET /api/v1/venues/facets` response.
 *
 * ## `unplacedVenues` is required, not decorative
 *
 * The neighborhood counts do **not** sum to `total`: roughly half of CLT's
 * venues fall inside no polygon (616 of 1,293 listable, measured on the
 * production branch 2026-09-09). Without this field the chip rail silently
 * implies a partition of the catalogue and visibly fails to reconcile with the
 * hero's "N venues across Charlotte". A consumer that renders the rail owes
 * the user that number.
 *
 * ## Overlapping polygons made the sum overshoot — a DATA defect (COR-93)
 *
 * Membership is `ST_Within` containment — the same predicate
 * `?neighborhoodSlug=` uses — so a venue inside two overlapping polygons
 * belongs to both chips and is counted twice. That made
 * `unplacedVenues + Σ neighborhood counts` overshoot `total`.
 *
 * Historical pre-partition production measurement (Neon `production`,
 * 2026-09-09): 12 overlapping CLT polygon pairs (`freemorewest` /
 * `wesley-heights` nested 99.2% of the smaller ring under PIP-176; COR-99
 * re-authored those rings), putting 40 of 1,293 listable venues inside 2+
 * polygons — Σ = 719 against 677 placed. Those figures are not current
 * facet totals.
 *
 * COR-93 partitioned stored polygons (authored ring minus every lower-position
 * ring) so a venue is inside at most one chip and
 * `unplacedVenues + Σ neighborhood counts = total` holds. This is
 * deliberately not compensated for in SQL. Switching to a single-assignment
 * rule would make the sum partition by breaking the guarantee below — the
 * chip would under-report what tapping it returns, which is the one failure
 * this endpoint exists to avoid. The identity is pinned by
 * `venueFacetsSql.db.test.ts` on the partitioned seed.
 *
 * ## `categories` must NOT be made to sum
 *
 * The 8 chips are independent predicates that legitimately overlap — a room
 * with both a gig and a tasting is in `music` AND `food-drink`. There is
 * deliberately no total on that array and nothing normalises it. Adding one
 * would assert a partition that does not exist.
 *
 * `outdoor` and `breweries` appear in both `categories` and `tiers` and carry
 * the same number in each: the two vocabularies mean the same venue-intrinsic
 * thing there, so the arrays are two views of one count set, not two counts.
 *
 * ## Every count equals what tapping the chip returns
 *
 * Each value is the `meta.total` a cursor-less `GET /api/v1/venues` returns
 * when filtered to that slug — structurally, because both endpoints build
 * their predicate with the same `venueBrowseWhereSql`. (On a *cursor* page
 * `meta.total` counts the rows remaining, so the first page is the comparable
 * call.)
 */
export const venueFacetsResponseSchema = z.object({
  neighborhoods: z.array(venueNeighborhoodFacetSchema),
  categories: z.array(venueCategoryFacetSchema),
  total: z.number().int().nonnegative(),
  unplacedVenues: z.number().int().nonnegative(),
  /**
   * An entry whose slug this bundle does not know is DROPPED, not fatal
   * (WEB-958 deploy-skew tolerance, as on `venueListItemSchema.category`):
   * `fetchVenueFacets` degrades a failed parse to "no counts", so one new
   * shelf slug from a newer backend would otherwise strip every chip count.
   */
  tiers: z
    .array(venueTierFacetSchema.nullable().catch(null))
    .transform((tiers) => tiers.filter((tier): tier is VenueTierFacet => tier !== null)),
  capacity: venueCapacityFacetSchema,
});
export type VenueFacetsResponse = z.infer<typeof venueFacetsResponseSchema>;

// ---------------------------------------------------------------------------
// API-1398: GET /api/v1/venues/:slug (detail)
// ---------------------------------------------------------------------------

/**
 * Every lane that may record a venue-fact observation (COR-60 / COR-61 / COR-83).
 *
 * CANONICAL — `services/data-pipeline/src/lib/venueFactProvenance.ts` imports
 * this array rather than declaring its own. It used to own the list, which put
 * the writer's vocabulary and the reader's vocabulary in different packages:
 * adding a lane there (`venue_site` was the last one) without widening the API
 * side would make `GET /venues/:slug` silently DROP that lane's observations on
 * read, and a dropped source line looks exactly like a venue nobody enriched.
 * One definition makes that unrepresentable.
 *
 * Values are the on-disk `venueFactProvenance[column].source` strings, so this
 * is a wire vocabulary — renaming an entry is a data migration, not a rename.
 */
export const VENUE_AMENITY_SOURCE_PROVIDERS = [
  'ticketmaster',
  'google_places',
  'openstreetmap',
  'venue_site',
] as const;

export const venueAmenitySourceProviderSchema = z.enum(VENUE_AMENITY_SOURCE_PROVIDERS);
export type VenueAmenitySourceProvider = z.infer<typeof venueAmenitySourceProviderSchema>;

/**
 * Per-fact attribution for one amenity card (API-1709).
 *
 * RAW, not pre-formatted. The design renders `TICKETMASTER · AUG 2026`, but the
 * API emits `{ provider, observedAt }` and lets the client build that string:
 * a month rendered server-side would bake in one locale and one timezone for
 * every consumer, and the web app is the only place that knows the reader's.
 *
 * **PIP-284 — this is a lookup STAMP, and nothing more.** `provider` is who was
 * asked and `observedAt` is when. Neither is Google Maps Content, so serving
 * them is unaffected by the closed caching list in
 * `docs/architecture/google-maps-content-caching.md`. Do NOT widen this object
 * with anything from a Places response — not `matchedStop.placeId`, not a
 * derived distance (§3.2.3(c) does not launder a derivation). Re-serving a
 * stored Places value would breach §3.2.3(a)(i) independently of the caching
 * clause, and this object is rendered on a public page.
 */
export const venueAmenitySourceSchema = z.object({
  provider: venueAmenitySourceProviderSchema,
  /** ISO-8601 instant the observation was recorded. */
  observedAt: z.string().datetime({ offset: true }),
});
export type VenueAmenitySource = z.infer<typeof venueAmenitySourceSchema>;

export const venueAmenitySchema = z.object({
  label: z.string(),
  value: z.string(),
  sub: z.string().nullable(),
  /**
   * `null` when no source is recorded for this fact — never a filler (API-1709).
   *
   * Two distinct cases both land here, and neither may be dressed up:
   *
   * 1. **A freeform `amenities[]` token.** `venueFactProvenance` is keyed by
   *    boolean COLUMN, so `wifi` / `coat_check` / `vip_areas` have no key at
   *    all. They are still source-confirmed — the Ticketmaster lane writes them
   *    in `mapTicketmasterAmenities` — just not per-fact stamped, so the card
   *    renders without a source line. Attributing them to
   *    `Location.enrichmentSource` was considered and REJECTED: that column is
   *    the Google Places processing sentinel (COR-60) and must never carry
   *    Ticketmaster, so it would print "GOOGLE PLACES" over a Ticketmaster fact.
   * 2. **A boolean whose observation predates the provenance column**, or whose
   *    stored stamp does not parse.
   *
   * A consumer MUST render nothing for `null`. An invented attribution on a
   * page whose own footnote reads "Only source-confirmed facts are listed" is
   * worse than a missing line.
   *
   * ## `.default(null)` is load-bearing — it is what stops a deploy from 404ing
   *
   * The asymmetry is deliberate: **required on output, optional on input.**
   * `z.infer` is the OUTPUT type, so `services/backend-api` still cannot build a
   * `VenueDetailResponse` without this key — the producer contract is unchanged
   * and the route stays type-checked. But `venueDetailResponseSchema.parse()`
   * now ACCEPTS a body where the key is absent, filling `null`.
   *
   * That matters because the web app hard-parses: `fetchVenueDetail` runs
   * `safeParse` and returns `null` on failure, and the page calls `notFound()`
   * on `null`. Zod has no per-field fallback, so ONE amenity object missing
   * `source` fails the whole response and 404s the venue page rather than
   * rendering it without source lines. A plain `.nullable()` therefore turns
   * three ordinary situations into an outage on a publicly live surface:
   *
   * - **Deploy skew.** Web (Vercel) and backend-api (Railway) deploy as
   *   independent pipelines off one `main` merge, with no ordering guarantee.
   *   Web landing first 404s every `/venue/{slug}` until Railway catches up —
   *   and a backend-only rollback does the same.
   * - **The Vercel edge.** The route sets
   *   `s-maxage=300, stale-while-revalidate=900`, and the origin
   *   `VENUES_DETAIL_CACHE_VERSION` bump does not reach it (see that constant's
   *   docblock), so a body cached seconds before the deploy is served for up to
   *   ~20 minutes after it.
   * - **Amplification.** `fetchVenueDetail` wraps the call in `unstable_cache`
   *   with `revalidate: 300`, which caches the resulting `null` — pinning each
   *   404 for a further 5 minutes after the backend is already correct.
   *
   * So: a new field on this response is added `.default(null)`, or an equally
   * tolerant reader, unless you have first established that no stale body can
   * reach a client. The cache-version bump closes only the origin half.
   */
  source: venueAmenitySourceSchema.nullable().default(null),
});
export type VenueAmenity = z.infer<typeof venueAmenitySchema>;

// COR-62 retired `Location.hasElevator` / `hasAudioAssistance` — no provider
// publishes either, so `elevator` / `audioAssist` could only ever be null.
// `wheelchair` keeps the COR-59 three-state contract: null = unknown, and a
// consumer must never render unknown as a negative accessibility claim.
export const venueAccessibilitySchema = z.object({
  wheelchair: z.boolean().nullable(),
});
export type VenueAccessibility = z.infer<typeof venueAccessibilitySchema>;

export const venueParkingSchema = z.object({
  hasParking: z.boolean().nullable(),
  note: z.string().nullable(),
});
export type VenueParking = z.infer<typeof venueParkingSchema>;

export const venueTransitSchema = z.object({
  accessibleByTransit: z.boolean().nullable(),
  nearestStopMeters: z.number().int().nonnegative().nullable(),
  lineNote: z.string().nullable(),
});
export type VenueTransit = z.infer<typeof venueTransitSchema>;

export const venueHouseRuleSchema = z.object({
  icon: z.string(),
  title: z.string(),
  body: z.string(),
});
export type VenueHouseRule = z.infer<typeof venueHouseRuleSchema>;

/**
 * Programming booleans that are **flags, not values** (COR-80).
 *
 * `booksDirect` / `openMic` arrive from the API-1396 seed as booleans, and a
 * boolean has exactly one side: the venue does this, or nothing is claimed.
 * They are emitted on {@link VenueProgrammingMeta.flags} and rendered as chips
 * — `[ BOOKS DIRECT ]` — never as a `label: value` row.
 *
 * Order is canonical: {@link venueProgrammingMetaSchema} always emits flags in
 * the order declared here, so the wire shape is deterministic and duplicate
 * input collapses for free.
 */
export const VENUE_PROGRAMMING_FLAG_KEYS = ['booksDirect', 'openMic'] as const;

export const venueProgrammingFlagSchema = z.enum(VENUE_PROGRAMMING_FLAG_KEYS);
export type VenueProgrammingFlag = z.infer<typeof venueProgrammingFlagSchema>;

/**
 * Chip copy per flag.
 *
 * SHARED ON PURPOSE, for the same reason as {@link VENUES_UPCOMING_WINDOW_DAYS}:
 * the wire carries the flag *key*, so every consumer that renders a chip is
 * asserting this label. A second copy in the web app is how COR-80 happened in
 * the first place — a label that had drifted into the value slot. One
 * definition makes the drift unrepresentable. `satisfies Record<...>` means a
 * new key fails `pnpm type-check` until it is given copy.
 */
export const VENUE_PROGRAMMING_FLAG_LABELS = {
  booksDirect: 'Books direct',
  openMic: 'Open mic',
} satisfies Record<VenueProgrammingFlag, string>;

/**
 * Wire/seed input for a programming flag field.
 *
 * - `true`  → the flag is active; surfaces in `flags`, and the fact side is `null`.
 * - `false` → nothing at all. COR-59: an absent boolean is "unknown", never a
 *   confident "no", so it must not render a greyed-out negative either.
 * - `string` → a genuine key/value fact ("Books direct: email bookings@…"),
 *   which keeps its own row. Blank strings normalize to `null`.
 * - `null`/absent → nothing.
 */
const programmingFlagInputSchema = z.union([z.string(), z.boolean(), z.null()]);

const normalizeProgrammingFact = (value: string | boolean | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isVenueProgrammingFlag = (value: unknown): value is VenueProgrammingFlag =>
  typeof value === 'string' && (VENUE_PROGRAMMING_FLAG_KEYS as readonly string[]).includes(value);

/**
 * Every recognised flag in `raw`, in canonical order, with anything else
 * dropped.
 *
 * **Lenient on purpose, and this is load-bearing.** `flags` is re-parsed
 * web-side as part of the whole `venueDetailResponseSchema`, so a closed
 * `z.enum` here would make one unrecognised entry fail the ENTIRE response:
 * `fetchVenueDetail` returns `null` on a parse failure and the page calls
 * `notFound()`. Adding a third flag key and shipping the backend (Railway)
 * ahead of the web build (Vercel) — they deploy independently — would then
 * 404 every affected venue's public page rather than omit one chip.
 *
 * Same posture, for the same reason, as `venueListCategoryQuerySchema`
 * degrading an unknown `?category=` to the unfiltered list instead of a 400:
 * on a public read path, a stale client degrades, it does not error.
 */
export const toVenueProgrammingFlags = (raw: unknown): VenueProgrammingFlag[] => {
  if (!Array.isArray(raw)) return [];
  const seen = new Set(raw.filter(isVenueProgrammingFlag));
  return VENUE_PROGRAMMING_FLAG_KEYS.filter((key) => seen.has(key));
};

export const venueProgrammingMetaSchema = z
  .object({
    booksDirect: programmingFlagInputSchema,
    openMic: programmingFlagInputSchema,
    /**
     * Accepted on input so the schema is **idempotent**. The same schema parses
     * the raw jsonb server-side AND re-parses the emitted response in
     * `apps/web/lib/server/fetchVenueDetail.ts`; on that second pass the source
     * booleans are already gone, so a re-derive-only transform would silently
     * drop every chip.
     *
     * Typed `unknown` rather than a closed enum array so a malformed or
     * newer-than-this-build value cannot fail the parse — see
     * {@link toVenueProgrammingFlags}, which does the narrowing.
     */
    flags: z.unknown(),
    genres: z.array(z.string()),
    avgRunTime: z.string().nullable(),
    contactEmail: z.string().email().nullable(),
  })
  .transform((meta) => {
    // A flag is active if this pass derived it from a boolean, or a previous
    // pass already emitted it. Filtering the canonical key list is what makes
    // the output ordered and duplicate-free without a second dedupe step.
    const carried = new Set(toVenueProgrammingFlags(meta.flags));

    return {
      booksDirect: normalizeProgrammingFact(meta.booksDirect),
      openMic: normalizeProgrammingFact(meta.openMic),
      flags: VENUE_PROGRAMMING_FLAG_KEYS.filter((key) => meta[key] === true || carried.has(key)),
      genres: meta.genres,
      avgRunTime: meta.avgRunTime,
      contactEmail: meta.contactEmail,
    };
  });
export type VenueProgrammingMeta = z.infer<typeof venueProgrammingMetaSchema>;

export const venueAddressSchema = z.object({
  line1: z.string(),
  line2: z.string().nullable(),
  phone: z.string().nullable(),
  hours: z.string().nullable(),
});
export type VenueAddress = z.infer<typeof venueAddressSchema>;

/**
 * Display labels for `Location.venueType` on the public venue-detail wire
 * (API-1721). The cover placard renders this string as a line, matching
 * `capacityNote` and amenity `label`s — display-ready copy, not the Prisma
 * token.
 *
 * COR-91: OTHER is a real verdict ("considered, nothing fit"), so it has
 * copy. NULL on the column is the omit. Emitting "Other" for a never-looked
 * row was the pre-COR-91 lie; the migration stores those as NULL instead.
 *
 * SHARED ON PURPOSE, for the same reason as {@link VENUE_PROGRAMMING_FLAG_LABELS}:
 * the wire carries the label, so a second map in web is how copy drifts.
 * `satisfies Record<VenueType, string>` fails `pnpm type-check` when a new
 * Prisma enum value lands without copy.
 */
export const VENUE_TYPE_KIND_LABELS = {
  CONCERT_HALL: 'Concert hall',
  PARK: 'Park',
  STADIUM: 'Stadium',
  THEATER: 'Theater',
  CLUB: 'Club',
  RESTAURANT: 'Restaurant',
  BAR: 'Bar',
  CONFERENCE_CENTER: 'Conference center',
  HOTEL: 'Hotel',
  MUSEUM: 'Museum',
  GALLERY: 'Gallery',
  OUTDOOR_SPACE: 'Outdoor space',
  COMMUNITY_CENTER: 'Community center',
  SCHOOL: 'School',
  UNIVERSITY: 'University',
  SPORTS_FACILITY: 'Sports facility',
  SHOPPING_CENTER: 'Shopping center',
  ARENA: 'Arena',
  PLACE_OF_WORSHIP: 'Place of worship',
  EVENT_SPACE: 'Event space',
  LIBRARY: 'Library',
  CINEMA: 'Cinema',
  OTHER: 'Other',
} satisfies Record<VenueType, string>;

export type VenueTypeKind = keyof typeof VENUE_TYPE_KIND_LABELS;

const isVenueTypeKind = (value: string): value is VenueTypeKind =>
  Object.prototype.hasOwnProperty.call(VENUE_TYPE_KIND_LABELS, value);

/**
 * Map a stored `Location.venueType` token onto the venue-detail `kind` line.
 *
 * Null / blank / an unrecognised token return `null` so a consumer can omit
 * the line. OTHER is a real label after COR-91. Unrecognised tokens must not
 * pass through as `CONCERT_HALL` — the placard prints the value as-is.
 *
 * This is **not** `classifyVenueKind` / `Location.kind` (COR-25's
 * address verdict). Same English word, different column.
 */
export const toVenueKindDisplayLabel = (venueType: string | null | undefined): string | null => {
  if (typeof venueType !== 'string') return null;
  const trimmed = venueType.trim();
  if (!isVenueTypeKind(trimmed)) return null;
  return VENUE_TYPE_KIND_LABELS[trimmed];
};

export const venueDetailResponseSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  cityCode: z.string(),
  neighborhoodSlug: z.string().nullable(),
  /**
   * `Location.postalCode` (API-1712). `null` when the column is null or holds
   * only whitespace — the route trims to null rather than emitting `''`, so a
   * consumer joining it into a line (`NODA · 28205`) cannot render a dangling
   * separator.
   *
   * Top-level rather than a fifth key on `address`, matching where
   * `coordinates` sits: `venueAddressSchema` is the shared postal-block shape
   * and every one of its four fields is a formatted display line, while this is
   * a raw datum the placard composes with `neighborhoodSlug`.
   *
   * `.default(null)`, not a bare `.nullable()`, for the deploy-skew reason
   * spelled out on {@link venueAmenitySchema}'s `source`: `fetchVenueDetail`
   * hard-`safeParse`s this response and the page calls `notFound()` on a
   * failure, so a required-but-absent key 404s live venue pages for the whole
   * Vercel-ahead-of-Railway window (and for the Vercel edge's
   * `stale-while-revalidate` tail after it). The OUTPUT type stays required —
   * the route always writes the key.
   */
  postalCode: z.string().nullable().default(null),
  /**
   * Humanised `Location.venueType` for the cover placard (API-1721).
   *
   * Display label (`"Concert hall"`), not the Prisma token (`CONCERT_HALL`):
   * the placard prints this line as-is, the same way it prints `capacityNote`.
   * `null` when the column is null (never classified). OTHER is a real
   * verdict after COR-91 and renders as `"Other"`.
   *
   * Named `kind` to match the placard prop. This is **not** `Location.kind`
   * (COR-25's resolved / unresolved-address verdict on the name).
   *
   * Typed as a freeform nullable string, not {@link venueTypeSchema}: a closed
   * enum here would 404 every venue page when a new Prisma value ships on
   * Railway ahead of web, the same deploy-skew `toVenueProgrammingFlags` exists
   * to prevent. The producer still maps through {@link toVenueKindDisplayLabel}.
   *
   * `.default(null)` for the same Vercel-ahead-of-Railway reason as
   * `postalCode`. The OUTPUT type stays required — the route always writes the
   * key.
   */
  kind: z.string().nullable().default(null),
  capacity: z.number().int().nonnegative().nullable(),
  capacityNote: z.string().nullable(),
  foundedYear: z.number().int().nullable(),
  editorialBlurb: z.string().nullable(),
  amenities: z.array(venueAmenitySchema),
  accessibility: venueAccessibilitySchema,
  parking: venueParkingSchema,
  transit: venueTransitSchema,
  houseRules: z.array(venueHouseRuleSchema),
  programmingMeta: venueProgrammingMetaSchema.nullable(),
  address: venueAddressSchema,
  coordinates: venueCoordinatesSchema.nullable(),
});
export type VenueDetailResponse = z.infer<typeof venueDetailResponseSchema>;

// ---------------------------------------------------------------------------
// API-1399: GET /api/v1/venues/:slug/shows (paginated upcoming events)
// ---------------------------------------------------------------------------

export const venueShowBadgeSchema = z.enum(['sold-out', 'almost-sold-out', 'curated', 'new']);
export type VenueShowBadge = z.infer<typeof venueShowBadgeSchema>;

export const venuesShowsQuerySchema = z.object({
  cursor: venueCursorSchema.optional(),
  limit: venueLimitSchema,
});
export type VenuesShowsQuery = z.infer<typeof venuesShowsQuerySchema>;

export const venueShowSchema = z.object({
  eventSlug: z.string(),
  title: z.string(),
  startsAt: z.string(),
  timezone: z.string(),
  supportText: z.string().nullable(),
  priceCents: z.number().int().nonnegative().nullable(),
  priceText: z.string(),
  badge: venueShowBadgeSchema.nullable(),
  /**
   * WEB-1098 — Ticketmaster resale (`tmr`) listing, by the shared PIP-343
   * predicate. Drives the DS 1.10.0 resale marker. Defaults to `false` when
   * absent so a payload from a server that predates the field still parses
   * (and renders no marker, exactly as before) instead of failing the page.
   */
  resaleListing: z.boolean().default(false),
});
export type VenueShow = z.infer<typeof venueShowSchema>;

export const venueShowsResponseSchema = z.object({
  shows: z.array(venueShowSchema),
  meta: venuePaginationMetaSchema,
});
export type VenueShowsResponse = z.infer<typeof venueShowsResponseSchema>;

// ---------------------------------------------------------------------------
// API-1400: GET /api/v1/venues/:slug/stats (derived stats)
// ---------------------------------------------------------------------------

export const venueStatsPriceRangeSchema = z.object({
  /** Lowest published band floor across upcoming paid shows. */
  minCents: z.number().int().nonnegative(),
  /**
   * Most expensive published tier across those same shows.
   *
   * Greater than `minCents` for every range this API emits, because the
   * producer collapses an equal-bounds range to `null` rather than shipping a
   * degenerate `$X – $X` (see `buildVenueStatsResponse`). That is a property of
   * how the value is built, NOT a constraint enforced here — deliberately.
   * This schema's only runtime use is the web client's `safeParse`, where a
   * `.refine()` would reject the whole payload and blank the stat strip
   * instead of letting the client collapse the range it can render correctly.
   * Both collapses are pinned by tests instead (API-1694).
   */
  maxCents: z.number().int().nonnegative(),
});
export type VenueStatsPriceRange = z.infer<typeof venueStatsPriceRangeSchema>;

export const venueStatsResponseSchema = z.object({
  upcomingCount: z.number().int().nonnegative(),
  /**
   * Days `upcomingCount` was computed over (API-1707). Defaulted for deploy
   * skew / stale Redis bodies the same way as `meta.upcomingWindowDays`.
   */
  upcomingWindowDays: z.number().int().positive().default(VENUES_UPCOMING_WINDOW_DAYS),
  /**
   * Mean published band floor across upcoming **paid** shows (API-1694). Free
   * and unknown-price shows are excluded, so a venue that runs a weekly free
   * open mic no longer reads cheaper than its tickets actually are. `null` when
   * the venue has no paid show in the window — read `freeShowCount` next, not
   * `0`.
   */
  avgShowPriceCents: z.number().int().nonnegative().nullable(),
  /**
   * Span of published ticket prices across those same paid shows. `null` when
   * there is no paid show, and also when every paid show sits at one figure —
   * `$18 – $18` is not a range, and `avgShowPriceCents` already says `$18`.
   */
  avgShowPriceRange: venueStatsPriceRangeSchema.nullable(),
  /** Upcoming shows with a published positive price tier. */
  paidShowCount: z.number().int().nonnegative(),
  /**
   * Upcoming shows that are deliberately free — the source declared free entry
   * or published a real `$0`/`$0` band. Distinct from an unknown price, which
   * is neither counted here nor in `paidShowCount`.
   */
  freeShowCount: z.number().int().nonnegative(),
  savedByCount: z.number().int().nonnegative(),
  yearsActive: z.number().int().nonnegative().nullable(),
});
export type VenueStatsResponse = z.infer<typeof venueStatsResponseSchema>;

// ---------------------------------------------------------------------------
// Cursor encoding helpers — shared so list, detail, shows endpoints keep the
// same opaque cursor format. Encoded as URL-safe base64 of a JSON tuple.
// ---------------------------------------------------------------------------

/**
 * A `/venues` cursor minted under the default `sort=name` ordering.
 *
 * `s` is absent on the wire. That is what keeps every cursor issued before
 * API-1705 valid: an old `{n,i}` payload decodes to this variant, and
 * {@link venueListCursorSort} reads it as `name`. Minting stays `{n,i}` too, so
 * the default path's cursor bytes are unchanged.
 */
export interface VenueListNameCursorPayload {
  readonly s?: 'name';
  /**
   * The `(name,id)` ordering key of the last row of the previous page:
   * {@link venueNameSortKey} of its `Location.name`, i.e. the name truncated
   * to {@link VENUE_NAME_SORT_KEY_MAX_CHARS} code points (WEB-876).
   *
   * Bounded rather than raw so the encoded cursor's length cannot depend on
   * the venue name's. It is compared against the same bounded expression in
   * SQL, so the keyset predicate still reproduces the ORDER BY exactly.
   */
  readonly n: string;
  /** Last `Location.id` returned in the previous page. */
  readonly i: string;
}

/**
 * A `/venues` cursor minted under `sort=upcoming`.
 *
 * Carries the ordering key explicitly (`y` = `Location.eventYield28d`, `null`
 * when the last row of the page had no snapshot) so the next page's keyset
 * predicate can be written against `(eventYield28d DESC NULLS LAST, id ASC)`,
 * and `s` so a cursor replayed under a DIFFERENT sort is caught rather than
 * silently mis-paginating — the whole set of rows either side of a `(name,id)`
 * cursor is unrelated to the set either side of a `(yield,id)` one, so
 * accepting the wrong sort's cursor would repeat and skip rows with a 200.
 */
export interface VenueListUpcomingCursorPayload {
  readonly s: 'upcoming';
  /** Last `Location.eventYield28d` returned in the previous page; `null` is the NULLS-LAST tail. */
  readonly y: number | null;
  /** Last `Location.id` returned in the previous page. */
  readonly i: string;
}

export type VenueListCursorPayload = VenueListNameCursorPayload | VenueListUpcomingCursorPayload;

/** Which ordering a decoded cursor was minted under. An absent `s` means `name`. */
export const venueListCursorSort = (payload: VenueListCursorPayload): VenuesListSort =>
  payload.s ?? 'name';

/**
 * Narrowing form of {@link venueListCursorSort} for the default ordering.
 *
 * A type predicate rather than a bare comparison because callers that reject
 * every non-`name` cursor then want to read `n` off what is left, and TypeScript
 * cannot follow the discriminant through a function's return type. Written
 * against `venueListCursorSort` so a third sort added later is excluded here
 * automatically, instead of falling through a hand-written `!== 'upcoming'`.
 */
export const isVenueListNameCursor = (
  payload: VenueListCursorPayload,
): payload is VenueListNameCursorPayload => venueListCursorSort(payload) === 'name';

export interface VenueShowsCursorPayload {
  /** Last `Event.date` ISO string returned in the previous page. */
  readonly s: string;
  /** Last `Event.id` returned in the previous page. */
  readonly i: string;
}

/**
 * `Location.eventYield28d` is Postgres `int4`, and the cursor key is bound as a
 * PARAMETER against that column (`"eventYield28d" < $n`). A bound value carries
 * no declared type, so Postgres infers `int4` from the comparison and raises
 * `22003 integer out of range` on anything wider — which the route's catch turns
 * into a **500 on a public, unauthenticated GET**, from a query string, repeatable
 * to the rate-limit budget. `Number.isInteger` alone does not stop that:
 * `3000000000` is a perfectly good JS integer.
 *
 * Verified against a real Postgres 16: `PREPARE p AS SELECT … WHERE
 * "eventYield28d" < $1; EXECUTE p(3000000000)` → `ERROR: integer out of range`.
 *
 * The lower bound is `0` rather than `INT32_MIN` because the column is a COUNT
 * of upcoming events — no writer can produce a negative, so a negative cursor
 * key is by definition forged. Rejecting it costs no legitimate caller a page.
 */
const VENUE_YIELD_CURSOR_MAX = 2_147_483_647;
const isVenueYieldCursorKey = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value <= VENUE_YIELD_CURSOR_MAX;

const toBase64Url = (input: string): string => {
  const base64 = Buffer.from(input, 'utf8').toString('base64');
  // Strip trailing '=' padding without `=+$` (CodeQL flags polynomial regex on
  // library-input flow). Buffer base64 output has at most 2 trailing '=' chars,
  // but the slice is robust regardless of length.
  let endIndex = base64.length;
  while (endIndex > 0 && base64.charCodeAt(endIndex - 1) === 0x3d) {
    endIndex--;
  }
  return base64.slice(0, endIndex).replace(/\+/g, '-').replace(/\//g, '_');
};

const fromBase64Url = (input: string): string => {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  const padding = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  return Buffer.from(padded + padding, 'base64').toString('utf8');
};

export const encodeVenueListCursor = (payload: VenueListCursorPayload): string =>
  toBase64Url(JSON.stringify(payload));

export const decodeVenueListCursor = (cursor: string): VenueListCursorPayload | null => {
  try {
    const parsed = JSON.parse(fromBase64Url(cursor)) as Record<string, unknown>;
    if (typeof parsed.i !== 'string') return null;

    // `s` absent → the pre-API-1705 `{n,i}` shape, i.e. the `name` ordering.
    const sort = parsed.s === undefined ? 'name' : parsed.s;

    if (sort === 'name') {
      if (typeof parsed.n !== 'string') return null;
      return { n: parsed.n, i: parsed.i };
    }

    if (sort === 'upcoming') {
      // `y` must be present and either an in-range integer or an explicit null
      // — an absent `y` is not the same claim as a null one (null IS the
      // NULLS-LAST tail position), so it is rejected rather than coerced.
      const y = parsed.y;
      if (y === null) return { s: 'upcoming', y: null, i: parsed.i };
      if (typeof y === 'number' && isVenueYieldCursorKey(y)) {
        return { s: 'upcoming', y, i: parsed.i };
      }
      return null;
    }

    // An unrecognised `s` is a cursor from a sort this build does not know.
    return null;
  } catch {
    return null;
  }
};

export const encodeVenueShowsCursor = (payload: VenueShowsCursorPayload): string =>
  toBase64Url(JSON.stringify(payload));

export const decodeVenueShowsCursor = (cursor: string): VenueShowsCursorPayload | null => {
  try {
    const parsed = JSON.parse(fromBase64Url(cursor)) as Partial<VenueShowsCursorPayload>;
    if (typeof parsed.s !== 'string' || typeof parsed.i !== 'string') return null;
    return { s: parsed.s, i: parsed.i };
  } catch {
    return null;
  }
};
